import React from "react";
import { Tag } from "antd";
import ExcelUploadStageModal, {
  ExcelUploadTypeConfig,
} from "../../../../components/upload/shared/ExcelUploadStageModal";
import { UploadReportState } from "../../../../components/upload/shared/uploadReport";
import { normaliseStockSheet, siteDailyApis } from "../../../../api/SiteDaily";
import { writeValidatedWorkbook } from "./filledWorkbook";
import {
  bucketRows,
  DownloadBlocked,
  FilledSheetDownload,
  messageColumns,
  moneyColumn,
  numberColumn,
  reportMessageColumns,
  reportStatus,
  rowColumn,
  sheetDate,
  statusColumn,
} from "./dailyUploadShared";

// Store-Stock upload — one row per material per day at the project store.
//
// The grain is the PROJECT-day, not a task-day: a store holds material for the
// whole project. Each date in the file is saved exactly as the sheet saves it (a
// replace-set for that project-day), and the commit then restates every later
// day of the materials it touched, because opening balances are stored.
//
// There is NO blank template, deliberately — the same rule as Material-Stores: a
// hand-typed sheet means hand-typed material names, and the filled day sheet
// exists to remove exactly that.
//
// And NO Civil pre-check, deliberately: stock is not consumption against the
// day's output. A store receives and issues on days nothing was built, so the
// only reason a day can refuse is a locked DPR.

// The filled sheet arrives with the store's whole material list on it, each row
// carrying its carried-forward opening. The clerk types the movement columns.
//
// Two rules hold this together:
//  * headers, widths and the row arrays are aligned BY POSITION only — the
//    writer reconciles nothing. Edit the three together.
//  * no column may normalise onto another's header key (the parser strips
//    bracketed units, lowercases and drops non-alphanumerics, and the LEFTMOST
//    column wins). Every name below is distinct once normalised: date, category,
//    material, uom, openingqty, openingrate, purchasedqty, purchasedrate,
//    issuedqty, issuedrate, remarks. Never a bare "Qty", "Rate" or "Unit".
//
// Kept deliberately short (user, Oct-2026): the adjustment pair and the computed
// (ref) tail — task issued qty, average rate, closing qty — are off the sheet.
// An adjustment is a correction, which belongs on the screen where the balance it
// corrects is visible; the computed columns are feedback, and the upload PREVIEW
// still shows every one of them per row before anything is saved.
const FILLED_HEADERS = [
  "Date",
  "Category (ref)",
  "Material",
  "UOM (ref)",
  "Opening Qty",
  "Opening Rate",
  "Purchased Qty",
  "Purchased Rate",
  "Issued Qty",
  "Issued Rate",
  "Remarks",
];
const FILLED_WIDTHS = [14, 26, 34, 14, 13, 13, 14, 14, 12, 13, 28];
const column = (header: string) => FILLED_HEADERS.indexOf(header) + 1;
// 1-based positions of the columns the store fills in — tinted so the sheet
// reads itself. Opening is among them, but only on a material's FIRST day: on
// every later row it is painted back to reference (see refCells below), because
// a carried opening cannot be overridden — corrections go through Adjustment.
const INPUT_COLUMNS = [
  column("Opening Qty"),
  column("Opening Rate"),
  column("Purchased Qty"),
  column("Purchased Rate"),
  column("Issued Qty"),
  column("Issued Rate"),
  column("Remarks"),
];

const INSTRUCTIONS: string[][] = [
  ["Store-Stock upload — Instructions"],
  [""],
  ["Date", "Required. Write it as 15-Sep-2026 (or 2026-09-15). One file may carry several dates."],
  ["Material", "Required. The product code (MAXMAT027) or the exact material name — matched within this project's own materials."],
  ["Opening Qty / Rate", "Only on a material's FIRST day, when nothing carries in. On every later row it comes down greyed: it is the closing of the last day that had a row, and an opening that contradicts it is rejected."],
  ["Purchased Qty", "What the store received that day. A receipt needs a Purchased Rate — otherwise the stock would be valued at nothing."],
  ["Issued Qty", "What the STORE issued that day. It is checked against what the tasks booked in Material-Stores and any difference is reported, but never blocked."],
  ["Issued Rate", "Optional — the rate the store issued at. The ledger VALUES the issue at the day's weighted average cost (that is what keeps the closing value tying), so this is recorded beside it and a gap of more than 1% is reported."],
  ["Two checks that only warn", "A Purchased Rate above the material's NORM price is flagged — every target costed on that material is understated by it. An Issued Qty above what the day's norms ALLOCATED to the tasks that actually produced is flagged too. Both save; neither blocks."],
  [""],
  ["What you type", "The amber columns. Everything marked (ref) is for reading only — the upload ignores it."],
  ["Damage and returns", "Not on this sheet. Record an adjustment on the Store-Stock screen, where the balance it corrects is in front of you."],
  ["How closing is worked out", "Closing = Opening + Purchased + Adjustment − Issued. Issues and adjustments are valued at the day's weighted average cost — (opening value + purchase value) ÷ (opening qty + purchase qty) — so the closing value always ties. Today's closing is the next day's opening."],
  ["Leave a line alone", "A row where you type nothing is skipped, so a filled sheet uploaded unchanged saves nothing — and the material keeps carrying its balance."],
  ["Blank numbers", "A blank Purchased, Issued or Adjustment is taken as 0, and the preview says so."],
  [""],
  ["Replace rule", "Each DATE in the file replaces what that day holds for the whole project: a material whose movement you remove is cleared for that day (the preview says how many). Dates not in the file are untouched."],
  ["Back-dated entries", "Allowed. Saving an earlier day restates every later day's opening and closing for the materials involved — the preview reports how many rows that is."],
  ["Negative closing", "Allowed, with a warning: it usually means a receipt has not been recorded yet. The closing quantity, the average cost and the variance against the task issues are all shown in the PREVIEW for every row before you commit."],
  ["Locked days", "A day whose daily progress report was submitted cannot be changed."],
  ["No Civil needed", "Unlike Material-Stores and Mechanical, a store day does not need Civil execution — a store receives and issues on days nothing was built."],
];

const num = (value: string) => (value === "" || value == null ? "" : Number(value));

const buildDaySheet = (projectId: number | string) => async (date: string): Promise<number> => {
  const res = await siteDailyApis.stockSheet(projectId, { date });
  if (res?.status !== true) throw new Error(res?.message || "Could not load the store's stock");
  const sheet = normaliseStockSheet(res.data);

  if (sheet.dayLocked) {
    throw new DownloadBlocked(
      `The daily progress report for ${sheetDate(date)} has been submitted, so the store's stock for that day is read-only.`,
      "This day is locked",
      "Pick another date, or have the day reopened before uploading stock for it.",
    );
  }
  if (!sheet.rows.length) {
    throw new DownloadBlocked(
      "This project's store has no materials yet — nothing can be stocked or issued.",
      "No materials in the store",
      "Apply material norms to the project's tasks first; the store then lists every material they consume.",
    );
  }

  const rows: any[][] = [];
  // Which Opening cells are read-only on THIS sheet: everything with a carried
  // balance. The column stays an input column (a first day has to be typable),
  // so the exceptions are painted per cell.
  const refCells: Array<[number, number]> = [];
  sheet.rows.forEach((row) => {
    rows.push([
      sheetDate(date),
      row.categoryName || "Uncategorized",
      // The NAME is what the clerk reads and what gets matched; a product code
      // is still accepted on a hand-made file.
      row.materialName,
      row.uomName,
      num(row.openingQty),
      num(row.openingRate),
      num(row.purchasedQty),
      num(row.purchasedRate),
      num(row.issuedQty),
      num(row.issuedRate),
      row.remarks,
    ]);
    if (row.carriedFrom !== "") {
      const excelRow = rows.length + 1; // row 1 is the header
      refCells.push([excelRow, column("Opening Qty")], [excelRow, column("Opening Rate")]);
    }
  });

  await writeValidatedWorkbook(
    `store-stock-${date}.xlsx`,
    {
      name: "Stock",
      headers: FILLED_HEADERS,
      widths: FILLED_WIDTHS,
      rows,
      inputColumns: INPUT_COLUMNS,
      refCells,
    },
    INSTRUCTIONS,
  );
  return rows.length;
};

const VARIANCE_LABEL: Record<string, { text: string; color: string }> = {
  MATCH: { text: "matches", color: "#2b9a66" },
  STORE_HIGHER: { text: "store higher", color: "#d9a545" },
  STORE_LOWER: { text: "tasks higher", color: "#cf1322" },
  NO_TASK_ISSUE: { text: "—", color: "#8094ae" },
  UNIT_MISMATCH: { text: "unit differs", color: "#8094ae" },
};

const previewColumns: any[] = [
  statusColumn,
  rowColumn,
  { title: "Date", dataIndex: "Date", key: "Date", width: 104 },
  { title: "Category", dataIndex: "Category", key: "Category", width: 170, ellipsis: true },
  {
    title: "Material",
    key: "material",
    width: 230,
    ellipsis: true,
    render: (_: any, row: any) => row.materialName || row.Material,
  },
  { title: "UOM", dataIndex: "UOM", key: "UOM", width: 90 },
  numberColumn("Opening", "OpeningQty", 100),
  numberColumn("Purchased", "PurchasedQty", 100),
  moneyColumn("Pur. rate", "PurchasedRate", 100),
  numberColumn("Issued", "IssuedQty", 95),
  moneyColumn("Issued rate", "IssuedRate", 100),
  numberColumn("Allocated", "TargetQty", 100),
  moneyColumn("Norm rate", "NormRate", 100),
  numberColumn("Adjust", "AdjustmentQty", 95),
  numberColumn("Booked", "TaskIssuedQty", 95),
  {
    title: "vs tasks",
    key: "variance",
    width: 120,
    render: (_: any, row: any) => {
      const label = VARIANCE_LABEL[row.Variance as string];
      if (!label) return <span style={{ color: "#8094ae" }}>—</span>;
      return <span style={{ color: label.color }}>{label.text}</span>;
    },
  },
  moneyColumn("Avg rate", "AvgRate", 100),
  numberColumn("Closing", "ClosingQty", 100),
  moneyColumn("Closing ₹", "ClosingValue", 120),
  { title: "Remarks", dataIndex: "Remarks", key: "Remarks", width: 170, ellipsis: true },
  ...messageColumns,
];

const days = (report: UploadReportState) =>
  new Set(report.rows.map((row: any) => row.Date)).size;

const StoreStockUploadModal = ({
  open,
  onClose,
  onSuccess,
  projectId,
  initialDate,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  projectId: number | string;
  initialDate?: string;
}) => {
  const config: ExcelUploadTypeConfig = React.useMemo(
    () => ({
      title: "Store-Stock — Excel upload",
      entityLabel: "Store stock",
      introSection: (
        <FilledSheetDownload
          label="Or start from a day's sheet"
          initialDate={initialDate}
          hint="Every material in the project's store, with its carried-forward opening balance already filled in. Type what was purchased and issued, then upload it back. No Civil execution is needed for a store day."
          build={buildDaySheet(projectId)}
        />
      ),
      startUpload: (formData) => siteDailyApis.uploadStockExcel(formData),
      buildBody: (comments) => ({ projectId: String(projectId), comment: comments ? comments : "N/A" }),
      previewColumns,
      previewScrollX: 2200,
      previewPageSize: 100,
      bucketRows,
      summaryTags: (report: UploadReportState) =>
        report.rows.length ? <Tag color="processing">{days(report)} day(s)</Tag> : null,
      fullScreen: true,
      confirmClose: true,
      reportExport: {
        fileName: "store-stock-upload-report.xlsx",
        columns: [
          { header: "Status", width: 12, value: reportStatus },
          { header: "Row", width: 8, value: (row: any) => row.Row ?? "" },
          { header: "Date", width: 12, value: (row: any) => row.Date || "" },
          { header: "Category", width: 22, value: (row: any) => row.Category || "" },
          { header: "Material", width: 32, value: (row: any) => row.materialName || row.Material || "" },
          { header: "UOM", width: 10, value: (row: any) => row.UOM || "" },
          { header: "Opening Qty", width: 12, value: (row: any) => row.OpeningQty ?? "" },
          { header: "Purchased Qty", width: 13, value: (row: any) => row.PurchasedQty ?? "" },
          { header: "Purchased Rate", width: 13, value: (row: any) => row.PurchasedRate ?? "" },
          { header: "Issued Qty", width: 12, value: (row: any) => row.IssuedQty ?? "" },
          { header: "Issued Rate", width: 12, value: (row: any) => row.IssuedRate ?? "" },
          { header: "Allocated by norms", width: 16, value: (row: any) => row.TargetQty ?? "" },
          { header: "Norm rate", width: 12, value: (row: any) => row.NormRate ?? "" },
          { header: "Adjustment Qty", width: 13, value: (row: any) => row.AdjustmentQty ?? "" },
          { header: "Task Issued Qty", width: 14, value: (row: any) => row.TaskIssuedQty ?? "" },
          { header: "vs tasks", width: 14, value: (row: any) => row.Variance || "" },
          { header: "Avg Rate", width: 12, value: (row: any) => row.AvgRate ?? "" },
          { header: "Closing Qty", width: 12, value: (row: any) => row.ClosingQty ?? "" },
          { header: "Closing Value", width: 14, value: (row: any) => row.ClosingValue ?? "" },
          ...reportMessageColumns,
        ],
      },
      rowErrorLabel: (row: any) => `Row ${row.Row ?? ""}`,
      copy: {
        uploadIntro: (
          <p>
            Start from the day's sheet below — it lists every material in the project's store with its{" "}
            <b>carried-forward opening balance</b> already filled in, and you type only what moved:{" "}
            <b>Purchased</b> and <b>Issued</b>, each with its rate. Closing, the average cost and the
            comparison against what the tasks booked are worked out for you and shown in the preview, and each
            date in the file <b>replaces what that day holds</b>.
          </p>
        ),
        validatingHeading: "Checking your file…",
        validatingSub:
          "Matching materials, carrying each opening balance forward and valuing the day exactly as the sheet's Save does.",
        committingHeading: "Saving the store's stock…",
        committingSub:
          "Writing each day and restating the days after it, in one transaction.",
        commitCta: "Save stock",
        doneHeading: "Store stock saved",
        doneSub: (report: UploadReportState) => (
          <>
            {report.rows.length} row(s) across {days(report)} day(s). Today's closing is now the next day's
            opening.
          </>
        ),
        creationFailedNote: "Nothing was saved — the whole file is one transaction.",
      },
      discardOnCancelFromPreview: false,
    }),
    [projectId, initialDate],
  );

  return (
    <ExcelUploadStageModal
      config={config}
      open={open}
      onClose={onClose}
      onSuccess={onSuccess}
      projectId={projectId}
    />
  );
};

export default StoreStockUploadModal;
